/** The Agents section's paths and the data its pages share (react-query keys stay shared). */
import { useMemo } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import ApiService, { ApiError } from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { FeatureFlag, useFeatureFlag } from '@/services/featureFlag.service';
import type { TenantUser } from '@/services/user.service';
import { emptyMember, type Connection, type TeamDoc } from './catalog';

export const AGENTS_PATH = '/agents';
export const teamPath = (id: string, tab?: string) => `${AGENTS_PATH}/teams/${id}${tab ? `/${tab}` : ''}`;
export const connectionPath = (id: string) => `${AGENTS_PATH}/connections/${id}`;

const api = () => ApiService.getInstance();

/** `waiting`: team id -> its approvals waiting for an Admin. Beside the documents, so an editor never saves it. */
export type Teams = { teams: TeamDoc[]; waiting?: Record<string, number> };

export function useTeams(enabled = true) {
  const apiReady = useApiAuthReady();
  return useQuery<Teams>({
    queryKey: ['agent-teams'],
    queryFn: async () => (await api().request('GET', '/agent-teams')) as Teams,
    enabled: apiReady && enabled,
  });
}

/** A saved version of an agent (teams.py version_json). Saving makes one; only one is live. */
export type TeamVersion = { number: number; live: boolean; created_by: string; created_at: string; document?: TeamDoc };

export function useTeamVersions(teamId: string | undefined) {
  const apiReady = useApiAuthReady();
  return useQuery<{ versions: TeamVersion[] }>({
    queryKey: ['agent-teams', teamId, 'versions'],
    queryFn: async () => (await api().request('GET', `/agent-teams/${teamId}/versions`)) as { versions: TeamVersion[] },
    enabled: apiReady && !!teamId,
  });
}

/** One version's document, as the editor edits it. Filled out, since one saved over MCP may leave fields out. */
export function useTeamVersion(teamId: string | undefined, number: number | null) {
  const apiReady = useApiAuthReady();
  return useQuery<TeamDoc>({
    queryKey: ['agent-teams', teamId, 'versions', number],
    queryFn: async () => {
      const v = (await api().request('GET', `/agent-teams/${teamId}/versions/${number}`)) as TeamVersion;
      const d = v.document!;
      return {
        id: teamId,
        name: d.name,
        description: d.description ?? '',
        members: d.members.map((m, i) => ({ ...emptyMember(i + 1), ...m })),
        edges: (d.edges ?? []).map((e) => ({ ...e, when: e.when ?? {} })),
      };
    },
    enabled: apiReady && !!teamId && number !== null,
    // Keep showing the version that was on screen while the next loads, rather than unmounting the editor.
    placeholderData: keepPreviousData,
  });
}

/**
 * Each agent is one user in Security & Governance: `agent:team-<id>`. Every step of it is scoped
 * as that user (src/services/agents/teams.py principal), and calls Pliable with a Builder's role.
 */
export const agentUserId = (teamId: string) => `agent:team-${teamId}`;
export const isAgentUser = (id: string) => id.startsWith('agent:team-');
const AGENT_ROLE = { id: 'agent', key: 'builder', name: 'Builder' };

/** The tenant's agents as users, to list beside people (agents-v2). Builder is the role their steps call Pliable with. */
export function useAgentUsers(): TenantUser[] {
  const teams = useTeams(useFeatureFlag(FeatureFlag.AGENTS_V2, false));
  return useMemo(
    () => (teams.data?.teams ?? []).map((t) => ({ id: agentUserId(t.id!), name: t.name, email: '', roles: [AGENT_ROLE] })),
    [teams.data],
  );
}

export function useConnections() {
  const apiReady = useApiAuthReady();
  return useQuery<{ connections: Connection[] }>({
    queryKey: ['agent-connections'],
    queryFn: async () => (await api().request('GET', '/agent-connections')) as { connections: Connection[] },
    enabled: apiReady,
  });
}

/**
 * A failed API call's message. FastAPI errors arrive as {code, error} (src/ui/error_handlers_asgi.py);
 * for 401, 403, 404 and 405 `error` is only a status word, so the caller's own message reads better.
 */
export function errorText(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError) || [401, 403, 404, 405].includes(e.code)) return fallback;
  return (e.payload as unknown as { error?: string } | undefined)?.error || fallback;
}

/** A Harness Service control call's error (the run viewer's; datahub passes the harness's `error`). */
export function controlErrorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const detail = (e.payload as unknown as { error?: string } | undefined)?.error;
    if (e.code === 404) return 'Not found, or not available on this stack.';
    if (e.code === 403) return 'Only Pliable staff can do that.';
    return detail || fallback;
  }
  return fallback;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** A harness run's status, from the control API's summary (agents/src/control.ts RunSummary). */
export type RunSummary = {
  status: string; // not started | running | completed | failed | aborted
  statusAt?: string; // when the latest submission started running, or settled
  startedAt?: string;
  costUsd?: number;
  error?: string;
};
export const isLive = (s: RunSummary) => s.status === 'running' || s.status === 'not started';

/** A run's summary under `base` (datahub's runs path for its kind), polled until it settles. */
const runSummaryQuery = (base: string, runId: string) => ({
  queryKey: ['agent-run', runId, 'summary'],
  queryFn: async () => (await api().request('GET', `${base}/${encodeURIComponent(runId)}/summary`)) as RunSummary,
  // Stop on an error too: a 404 (a run past the harness's window, an older Harness Service) won't change.
  refetchInterval: (q: { state: { data?: RunSummary; status: string } }) =>
    q.state.status === 'error' || (q.state.data && !isLive(q.state.data)) ? false : 2_000,
  retry: false, // an older Harness Service has no summary; the viewer copes
});

export function useRunSummary(base: string, runId: string) {
  return useQuery<RunSummary>(runSummaryQuery(base, runId));
}

/** Several runs' statuses by id: undefined while loading (or from an older Harness Service). */
export function useRunStatuses(base: string, runIds: string[]): (runId: string) => string | undefined {
  const statuses = useQueries({
    queries: runIds.map((id) => runSummaryQuery(base, id)),
    combine: (rs) => new Map(rs.map((r, i) => [runIds[i]!, r.data?.status])),
  });
  return (id) => statuses.get(id);
}
